import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Download, CalendarDays, HardDrive, ArrowLeft, CheckCircle, Link as LinkIcon, Mail, MessageSquare, Globe, Send, Phone, Info, Volume2, AlertCircle, HeartHandshake, Gift } from 'lucide-react';
import AdPlaceholder from '@/components/AdPlaceholder';
import { showSuccess, showError } from '@/utils/toast';
import { Progress } from "@/components/ui/progress";
import ImageGallery from '@/components/ImageGallery'; // Importáljuk az új komponenst
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { formatBytes } from '../utils/format-bytes';
import { useAuth } from "@/context/AuthContext";
import { useDownloadSettings } from '@/hooks/useDownloadSettings';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";


interface Picture {
  url: string;
  blobId: string;
}

interface Program {
  id: number;
  name: string;
}

interface DownloadEntry {
  id: number;
  version: string;
  uploadDate: string;
  game_id: number;
  gameName: string;
  gameSlug: string;
  real_uploader: string;
  size: string;
  uploader: string;
  uploader_ids: number[];
  pictures: Picture[];
  uploaderprograms: Program[];
  description: string;
  demo: boolean;
  is_anonymous: boolean;
  banwarning: boolean;
  creatorId: string;
  downloadsCount: number;
  slug: string;
  fileName?: string;
  isCompleted?: boolean;
}

interface ContactLink {
  type: string;
  value: string;
}

interface SupportLink {
  type: 'paypal' | 'buymeacoffee' | 'crypto' | 'bank' | 'patreon' | 'steam' | 'revolut' | 'discord' | 'other' | 'ko-fi';
  url: string;
  label?: string;
  iconbootstrap?: string;
}

const formatDownloadSize = (size: string | number): string => {
  if (typeof size === 'number') {
    return formatBytes(size);
  }
  // Check if the string is a valid number (and not just whitespace)
  const sizeAsNumber = Number(size);
  if (!isNaN(sizeAsNumber) && String(size).trim() !== '' && sizeAsNumber > 0) {
    return formatBytes(sizeAsNumber);
  }
  // Otherwise, return the pre-formatted string or a default value
  return size || 'N/A';
};

// Helper functions for icons and display names
const getContactLinkIcon = (type: string) => {
  switch (type.toLowerCase()) {
    case 'email': return <Mail size={18} />;
    case 'discord': return <MessageSquare size={18} />;
    case 'weboldal': return <Globe size={18} />;
    case 'telegram': return <Send size={18} />;
    case 'telefon': return <Phone size={18} />;
    default: return <LinkIcon size={18} />;
  }
};

const getSupportLinkIcon = (type: SupportLink['type']) => {
  switch (type) {
    case 'paypal': return <i className="bi bi-paypal"></i>;
    case 'buymeacoffee': return <i className="bi bi-cup-hot"></i>; // nincs buymeacoffee, kávés ikon helyette
    case 'ko-fi': return <i className="bi bi-cup-hot"></i>; // nincs buymeacoffee, kávés ikon helyette
    case 'crypto': return <i className="bi bi-currency-bitcoin"></i>;
    case 'bank': return <i className="bi bi-bank"></i>;
    case 'patreon': return <i className="bi bi-heart"></i>; // nincs patreon, szív ikon helyette
    case 'steam': return <i className="bi bi-steam"></i>;
    case 'revolut': return <i className="bi bi-credit-card"></i>; // nincs revolut, kártya ikon helyette
    case 'discord': return <i className="bi bi-discord"></i>;
    case 'other': return <i className="bi bi-credit-card-2-front"></i>;
    default: return null;
  }
};

const getSupportLinkDisplayName = (link: SupportLink) => {
  if (link.type === 'other' && link.label) {
    return link.label;
  }
  switch (link.type) {
    case 'paypal': return 'PayPal';
    case 'buymeacoffee': return 'Buy Me a Coffee';
    case 'crypto': return 'Kripto';
    case 'bank': return 'Banki átutalás';
    case 'patreon': return 'Patreon';
    case 'steam': return 'Steam';
    case 'revolut': return 'Revolut';
    case 'ko-fi': return 'Ko-fi';
    case 'discord': return 'Discord';
    default: return 'Ismeretlen';
  }
};

interface ThankYouEntry {
  id: string;
  user: string;
  message: string;
  date: string;
  avatar_url?: string;
  translater?: boolean;
  admin?: boolean;
}

const truncateName = (name: string, maxLength: number = 8): string => {
  if (name.length <= maxLength) return name;
  return name.substring(0, maxLength) + '...';
};

const DownloadShow: React.FC = () => {
  const { isAdmin, isTranslator, isLoggedIn, currentUser } = useAuth();
  const { registeredOnly } = useDownloadSettings();
  const blockGuestDownloads = !isLoggedIn && registeredOnly;
  const { id } = useParams<{ id: string }>();
  const DOWNLOAD_COOLDOWN_SECONDS = 40;
  const INITIAL_COUNTDOWN = 10;
  const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
  const [countdown, setCountdown] = useState(isTranslator ? 0 : INITIAL_COUNTDOWN);
  const [showDownloadButton, setShowDownloadButton] = useState(isTranslator);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [download, setDownload] = useState<DownloadEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creators, setCreators] = useState<any[]>([]);

  // Új állapotok a CAPTCHA-hoz
  const [captchaCode, setCaptchaCode] = useState('');
  const [captchaInput, setCaptchaInput] = useState('');
  const [isCaptchaVerified, setIsCaptchaVerified] = useState(false);

  // Új állapotok a letöltés és a globális időzár kezelésére
  const [isDownloading, setIsDownloading] = useState(false);
  const [isOnCooldown, setIsOnCooldown] = useState(false);
  const [cooldownTimeLeft, setCooldownTimeLeft] = useState(0);
  const cooldownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const audioRef = useRef(new Audio('/1.mp3'));
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);

  // Köszönöm rendszer state-ek
  const [thankYous, setThankYous] = useState<ThankYouEntry[]>([]);
  const [hasThanked, setHasThanked] = useState(false);
  const [thanksLoading, setThanksLoading] = useState(true);

  const userHasValidName = React.useMemo(() => {
    if (!currentUser?.name) return false;
    return !/^\d+$/.test(currentUser.name);
  }, [currentUser]);

  // This effect handles cleanup and audio events.
  useEffect(() => {
    const audio = audioRef.current;
    const handleAudioEnd = () => setIsAudioPlaying(false);

    audio.addEventListener('ended', handleAudioEnd);
    audio.loop = false;

    // Cleanup on unmount
    return () => {
      audio.pause();
      audio.currentTime = 0;
      audio.removeEventListener('ended', handleAudioEnd);
    };
  }, []); // Empty dependency array ensures this only runs on mount and unmount.

  useEffect(() => {
    const fetchData = async () => {
      try {
        const resDownload = await fetch(`/api/translations/${id}`);
        if (!resDownload.ok) throw new Error("Letöltés nem található.");
        const downloadData = await resDownload.json();
        setDownload(downloadData);
      } catch (err: any) {
        setError(err.message || "Hiba történt az adatok betöltésekor.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  // CAPTCHA kód generálása a komponens betöltésekor
  useEffect(() => {
    setCaptchaCode(Math.floor(1000 + Math.random() * 9000).toString());
  }, []);

  // useEffect a document title és meta description beállításához
  useEffect(() => {
    if (download?.gameName) {
      document.title = `${download.gameName} Magyarítás Letöltés (PC) – Magyar Fordítás | GEP.MONSTER`;

      // Meta description beállítása
      const metaDescription = `${download.gameName} magyarítás letöltés PC-re. Magyar fordítás, telepítési információk és kompatibilis verziók egy helyen a GEP.MONSTER oldalán.`;
      let metaTag = document.querySelector('meta[name="description"]');
      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.setAttribute('name', 'description');
        document.head.appendChild(metaTag);
      }
      metaTag.setAttribute('content', metaDescription);
    }
  }, [download?.gameName]);

  const checkCooldown = useCallback(() => {
    if (cooldownIntervalRef.current) {
      clearInterval(cooldownIntervalRef.current);
    }

    const lastDownloadTimestamp = localStorage.getItem('lastDownloadTimestamp');
    if (lastDownloadTimestamp) {
      const timePassed = Date.now() - parseInt(lastDownloadTimestamp, 10);
      if (timePassed < DOWNLOAD_COOLDOWN_SECONDS * 1000) {
        const timeLeft = Math.ceil((DOWNLOAD_COOLDOWN_SECONDS * 1000 - timePassed) / 1000);
        setCooldownTimeLeft(timeLeft);
        setIsOnCooldown(true);

        cooldownIntervalRef.current = setInterval(() => {
          setCooldownTimeLeft(prev => {
            if (prev <= 1) {
              if (cooldownIntervalRef.current) clearInterval(cooldownIntervalRef.current);
              setIsOnCooldown(false);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        setIsOnCooldown(false);
      }
    }
  }, [DOWNLOAD_COOLDOWN_SECONDS]);

  useEffect(() => {
    checkCooldown();

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'lastDownloadTimestamp') {
        checkCooldown();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      if (cooldownIntervalRef.current) clearInterval(cooldownIntervalRef.current);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [checkCooldown]);

  useEffect(() => {
    // Ha a felhasználó fordító, azonnal jelenítsük meg a gombot.
    // Ez kezeli azt az esetet, amikor a felhasználói státusz később töltődik be.
    if (isTranslator) {
      setShowDownloadButton(true);
      return;
    }

    // Ha a gomb már látható, vagy a visszaszámláló még nem indult el, ne csináljunk semmit.
    if (showDownloadButton) return;

    if (countdown <= 0) {
      setShowDownloadButton(true);
      return;
    }

    // Visszaszámláló logika
    const timer = setTimeout(() => setCountdown(prev => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown, isTranslator, showDownloadButton]);

  useEffect(() => {
    const fetchCreators = async () => {
      if (!download?.uploader_ids) return;
      try {
        const promises = download.uploader_ids.map((id) =>
          fetch(`/api/translators/${id}`).then((res) => res.ok ? res.json() : null)
        );
        const results = await Promise.all(promises);
        const validCreators = results.filter(Boolean);
        setCreators(validCreators);
      } catch (err) {
        console.error("Hiba a fordítók lekérésekor", err);
      }
    };
    fetchCreators();
  }, [download]);

  // Köszöneteket lekérő effect
  useEffect(() => {
    if (!download?.game_id) return;

    const fetchThanks = async () => {
      setThanksLoading(true);
      try {
        const res = await fetch(`/api/thanks?game_id=${download.game_id}`);
        if (!res.ok) throw new Error('Nem sikerült lekérni a köszöneteket');

        const data = await res.json();

        const apiThanks: ThankYouEntry[] = data.users.map((u: any) => ({
          id: `api-${u.id}`,
          user: u.name,
          message: 'Köszönöm a munkátokat!',
          date: '',
          avatar_url: u.avatar_url || undefined,
          translater: u.translater,
          admin: u.admin
        }));

        setThankYous(apiThanks);
        setHasThanked(data.already_thanked || false);
      } catch (error: any) {
        showError(error.message || 'Hiba a köszönetek lekérésekor');
        setThankYous([]);
        setHasThanked(false);
      } finally {
        setThanksLoading(false);
      }
    };

    fetchThanks();
  }, [download?.game_id]);

  const handlePlayAudio = () => {
    const audio = audioRef.current;
    if (isAudioPlaying) {
      audio.pause();
      audio.currentTime = 0;
      setIsAudioPlaying(false);
    } else {
      audio.play().catch(error => {
        console.warn("Audio play was prevented.", error);
        showError("A hang lejátszása nem indítható el automatikusan.");
      });
      setIsAudioPlaying(true);
    }
  };

  const handleVerifyCaptcha = () => {
    if (captchaInput === captchaCode) {
      showSuccess('Kód elfogadva! A letöltés engedélyezve.');
      setIsCaptchaVerified(true);
    } else {
      showError('A beírt kód helytelen. Próbáld újra!');
      setCaptchaInput('');
    }
  };

  const handleDownloadClick = async () => {
    if (isDownloading || isOnCooldown || !download || blockGuestDownloads) return;
    setIsDownloading(true);

    try {
      const res = await fetch(`/api/downloads/start/${download.id}`, {
        method: "POST",
        headers: { "X-CSRF-Token": token, "Content-Type": "application/json" },
      });

      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error || `Hiba a letöltés indításakor (HTTP ${res.status}).`);
      }

      const data = await res.json();

      if (data.url) {
        showSuccess(`A letöltés elindult!`);
        localStorage.setItem('lastDownloadTimestamp', Date.now().toString());
        checkCooldown(); // Azonnal elindítjuk a visszaszámlálást

        const link = document.createElement("a");
        link.href = data.url;
        link.download = download.fileName || "file";
        document.body.appendChild(link);
        link.click();
        link.remove();
      } else {
        throw new Error("Nem kaptunk letöltési URL-t.");
      }
    } catch (err) {
      console.error(err);
      const reason = err instanceof Error ? err.message : "";
      alert(`Nem sikerült elindítani a letöltést.${reason ? `\n${reason}` : ""}`);
    } finally {
      setTimeout(() => setIsDownloading(false), 3000);
    }
  };

  const handleThankYou = async () => {
    if (!isLoggedIn) {
      showError('A köszönöm üzenet küldéséhez be kell jelentkeznie!');
      return;
    }
    if (!userHasValidName) {
      showError('A köszönöm üzenet küldéséhez be kell állítanod egy nevet a profilodban!');
      return;
    }

    if (hasThanked) {
      showError('Már megköszönted ezt a fordítást!');
      return;
    }

    if (!download) return;

    try {
      const res = await fetch('/api/thanks', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': token
        },
        body: JSON.stringify({ game_id: download.game_id })
      });

      if (res.status === 201) {
        const newThankYou: ThankYouEntry = {
          id: `t${Date.now()}`,
          user: currentUser?.translaterName || currentUser?.alias || currentUser?.name || 'Névtelen',
          message: 'Köszönöm a munkátokat!',
          date: new Date().toLocaleString('hu-HU'),
          avatar_url: currentUser?.avatar_url || undefined,
        };
        setThankYous([newThankYou, ...thankYous]);
        setHasThanked(true);
        showSuccess('Köszönöm üzenet elküldve!');
      } else {
        const errorData = await res.json();
        showError(errorData.error || 'Nem sikerült elküldeni a köszönetet.');
      }
    } catch (e: any) {
      showError(e.message || 'Hiba történt a köszönet elküldésekor.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <div className="sticky top-0 z-50 w-full">
          <Header />
        </div>
        <main className="flex-grow flex items-center justify-center text-muted-foreground mt-[12px]">
          <p>Betöltés...</p>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !download) {
    return (
      <div className="min-h-screen flex flex-col">
        <div className="sticky top-0 z-50 w-full">
          <Header />
        </div>
        <main className="flex-grow container mx-auto p-4 py-8 text-center text-muted-foreground mt-[12px]">
          <h1 className="text-3xl font-bold text-primary mb-4">Hiba történt</h1>
          <p className="text-xl text-gray-600 mb-4">{error || "Letöltés nem található."}</p>
          <Link to="/" className="text-blue-500 hover:text-blue-700 underline">Vissza a főoldalra</Link>
        </main>
        <Footer />
      </div>
    );
  }

  const exampleImages = download.pictures.map((pic) => pic.url);

  const openGallery = (index: number) => {
    if (exampleImages.length > 0) {
      setSelectedImageIndex(index);
      setIsGalleryOpen(true);
    }
  };
  return (
    <TooltipProvider>
      <div className="min-h-screen flex flex-col">
        <div className="sticky top-0 z-50 w-full">
          <Header />
        </div>
        <main className="flex-grow container mx-auto p-4 py-8 main-content-bg mt-[12px]">
          {isAdmin && (
            <AdPlaceholder height="h-32" text="Letöltés oldal felső reklám" className="mb-8" />
          )}
          <Link to={`/games/${download.slug}`} className="flex items-center text-primary hover:text-primary/40 transition-colors mb-6">
            <ArrowLeft size={20} className="mr-2" /> Vissza a játékhoz
          </Link>
          <Card className="bg-card text-card-foreground shadow-lg">

            <CardHeader>
              <CardTitle className="text-4xl font-bold text-primary mb-4">Verzió: {download.fileName || download.version}</CardTitle>
              <h1 className="text-2xl font-semibold text-gray-200 mb-3">{download.gameName} Magyarítás</h1>
              {isAdmin && (
                <p className="text-lg text-muted-foreground">
                  Feltöltő: <span className="font-semibold text-foreground">
                    {download.real_uploader}
                  </span>
                </p>
              )}
            </CardHeader>

            <CardContent className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 flex flex-col gap-6">
                {download.pictures.length > 0 && (
                  <div>
                    <div className="mb-6">
                      <img
                        src={exampleImages[selectedImageIndex]}
                        alt={`Példa kép ${selectedImageIndex + 1}`}
                        className="w-full h-auto rounded-lg shadow-md object-cover cursor-pointer mb-4"
                        onClick={() => openGallery(selectedImageIndex)}
                        decoding="async"
                        crossOrigin="anonymous"
                        loading="eager"
                        style={{ WebkitTouchCallout: 'none', WebkitUserSelect: 'none' } as any}
                      />
                      <div className="flex flex-wrap gap-2 justify-center">
                        {exampleImages.map((img, index) => (
                          <img
                            key={index}
                            src={img}
                            alt={`Bélyegkép ${index + 1}`}
                            className={`w-24 h-16 object-cover rounded-md cursor-pointer border-2 ${selectedImageIndex === index ? 'border-primary' : 'border-transparent'} hover:border-primary transition-colors`}
                            onClick={() => setSelectedImageIndex(index)}
                            decoding="async"
                            crossOrigin="anonymous"
                            loading="lazy"
                            style={{ WebkitTouchCallout: 'none', WebkitUserSelect: 'none' } as any}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                <Card className="bg-secondary text-secondary-foreground border-border shadow-md">
                  <CardHeader>
                    <CardTitle className="text-xl font-bold text-primary">Letöltés adatai</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm text-muted-foreground">

                    <div className="flex flex-wrap gap-6">
                      <p className="flex items-center gap-2">
                        <HardDrive size={18} className="text-green-400" />
                        <span className="font-semibold text-foreground">Méret:</span> {formatDownloadSize(download.size)}
                      </p>

                      <p className="flex items-center gap-2">
                        <CalendarDays size={18} className="text-cyan-400" />
                        <span className="font-semibold text-foreground">Feltöltve:</span> {download.uploadDate}
                      </p>

                      <p className="flex items-center gap-2">
                        <Download size={18} className="text-cyan-400" />
                        <span className="font-semibold text-foreground">Letöltve:</span> {download.downloadsCount}
                      </p>

                      {download.demo && (
                        <p className="flex items-center gap-2">
                          <CheckCircle
                            size={18}
                            className={download.demo ? "text-green-500" : "text-red-500"}
                          />
                          <span className="font-semibold text-foreground">Státusz:</span>
                          {download.demo ? 'Demo' : ''}
                        </p>
                      )}
                    </div>

                    <div className="bg-gray-800 text-green-300 border border-yellow-700 p-3 rounded-lg my-4 text-sm">
                      <div className="flex justify-between items-center mb-2">
                        <p className="font-semibold">Figyelem!</p>
                        <Button variant="secondary" size="sm" onClick={handlePlayAudio} className="animate-pulse">
                          {isAudioPlaying ? <Volume2 size={18} className="mr-2" /> : <Info size={18} className="mr-2" />}
                          Tudnivalók
                        </Button>
                      </div>
                      <p>Letöltésemmel elfogadom, hogy ezek a fordítások GÉPPEL készültek. A fordítás tartalmazhat: félrefordításokat, angolul maradt szövegelemeket, erősen befolyásolhatják a játékélményt! Csak a készítő által megjelölt verzióra használható!</p>
                    </div>
                    {download.banwarning && (
                      <div className="bg-red-900/30 text-red-300 border border-red-700 p-3 rounded-lg my-4 text-sm">
                        <div className="flex items-center gap-2 mb-2">
                          <AlertCircle size={20} className="text-red-400" />
                          <p className="font-semibold">⚠️ Ban Figyelmeztetés</p>
                        </div>
                        <p>Ezt a fordítást az <b>anticheat</b> rendszer veszélyesnek ítélheti! Bár senki sem jelezett még problémát, <span className="font-semibold">erősen ajánlott, hogy kerüld el a játékban az online részek használatát</span> ezzel a fordítással!</p>
                      </div>
                    )}
                    <div className="border-t border-border pt-6 text-center min-h-[130px]">
                      {blockGuestDownloads ? (
                        <div className="flex flex-col items-center gap-3 text-center">
                          <Button disabled className="w-full bg-primary text-primary-foreground text-lg py-3 disabled:opacity-50">
                            <Download size={20} className="mr-2" /> Letöltés indítása
                          </Button>
                          <p className="text-sm text-muted-foreground">A letöltéshez regisztráció és bejelentkezés szükséges.</p>
                        </div>
                      ) : isOnCooldown ? (
                        <div className="text-center text-green-300 p-4 bg-destructive/10 rounded-lg">
                          <p className="text-sm">A következő letöltésig várnod kell: {cooldownTimeLeft} másodpercet.</p>
                        </div>
                      ) : !showDownloadButton ? (
                        <>
                          <p className="mb-2 text-lg font-semibold">Letöltés előkészítése...</p>
                          <div className="flex items-center justify-center my-4">
                            <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center text-primary text-3xl font-bold">
                              {countdown}
                            </div>
                          </div>
                          <p className="text-sm mb-4">A letöltés {countdown} másodperc múlva lesz elérhető</p>
                          <Progress value={((INITIAL_COUNTDOWN - countdown) / INITIAL_COUNTDOWN) * 100} className="w-full h-2 bg-muted-foreground/20 [&::-webkit-progress-bar]:rounded-lg [&::-webkit-progress-value]:rounded-lg [&::-webkit-progress-value]:bg-primary" />
                        </>
                      ) : !isCaptchaVerified ? (
                        <div className="flex flex-col items-center gap-4">
                          <Label htmlFor="captcha-input" className="text-lg font-semibold">A letöltéshez írd be az alábbi számot:</Label>
                          <div className="bg-muted text-foreground text-3xl font-bold tracking-widest p-3 rounded-md border border-border select-none">
                            {captchaCode}
                          </div>
                          <div className="flex gap-2 w-full max-w-xs">
                            <Input
                              id="captcha-input"
                              type="text"
                              value={captchaInput}
                              onChange={(e) => setCaptchaInput(e.target.value)}
                              placeholder="Írd be a számot..."
                              className="text-center text-lg"
                              maxLength={4}
                            />
                            <Button onClick={handleVerifyCaptcha}>Ellenőrzés</Button>
                          </div>
                        </div>
                      ) : (
                        <Button
                          onClick={handleDownloadClick}
                          disabled={isDownloading || isOnCooldown || !isCaptchaVerified}
                          className="w-full bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground text-lg py-3 disabled:opacity-50"
                        >
                          {isDownloading ? 'Folyamatban...' : <><Download size={20} className="mr-2" /> Letöltés indítása</>}
                        </Button>
                      )}

                      {creators.some((c) => c.supportLinks && c.supportLinks.length > 0) && (
                        <div className="mt-8">
                          <h3 className="text-xl font-bold text-foreground mb-2">Támogasd a {creators.length === 1 ? 'készítőt' : 'készítőket'}!</h3>
                          <p className="text-muted-foreground mb-4">
                            Ha van lehetőséged, támogasd a {creators.length === 1 ? 'munkáját' : 'munkájukat'}.
                          </p>

                          <div className="flex flex-wrap justify-center gap-4">
                            {creators
                              .filter((creator) => creator.supportLinks && creator.supportLinks.length > 0)
                              .map((creator) => (
                                <div
                                  key={creator.id}
                                  className="bg-card p-4 rounded-lg border border-border flex flex-col items-center text-center"
                                > <a href={`/translator/${creator.slug}`}>
                                    <Avatar className="w-16 h-16 mb-2 border-2 border-primary">
                                      <AvatarImage src={creator.avatar} alt={creator.name} crossOrigin="anonymous" decoding="async" />
                                      <AvatarFallback>{creator.name.charAt(0)}</AvatarFallback>
                                    </Avatar>
                                  </a>
                                  <a href={`/translator/${creator.slug}`}>
                                    <p className="font-semibold text-foreground">{creator.name}</p>
                                  </a>
                                  <div className="flex flex-wrap gap-2 mt-2 justify-center">
                                    {creator.supportLinks.map((link: SupportLink, index: number) => (
                                      <Tooltip key={index}>
                                        <TooltipTrigger asChild>
                                          <Button
                                            variant="outline"
                                            size="icon"
                                            className="bg-muted text-white border-border h-9 w-9"
                                            asChild
                                          >
                                            <a href={link.url} target="_blank" rel="noopener noreferrer">
                                              {link.iconbootstrap ? (
                                                <span dangerouslySetInnerHTML={{ __html: link.iconbootstrap }} />
                                              ) : (
                                                getSupportLinkIcon(link.type)
                                              )}
                                            </a>
                                          </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          <p>{getSupportLinkDisplayName(link)}</p>
                                        </TooltipContent>
                                      </Tooltip>
                                    ))}
                                  </div>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Köszönöm Card */}
                <Card className="bg-gray-800 text-secondary-foreground border-border shadow-md">
                  <CardHeader>
                    <div className="flex items-center">
                      <HeartHandshake size={20} className="mr-2 text-primary" />
                      <CardTitle className="text-xl font-bold text-primary">Köszönöm</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="text-center mb-8">
                      <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Gift className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="text-2xl font-bold text-white mb-2">Köszönöm a fordítást!</h3>
                      <p className="text-gray-400 max-w-2xl mx-auto">A fordítóink rengeteg időt és energiát fektetnek abba, hogy kedvenc játékaidat magyar nyelven élvezhesd. Néha egy köszönöm sokat jelent nekik a munka folytatásában!</p>
                    </div>

                    {!isLoggedIn && (
                      <p className="text-center text-red-400 font-semibold mb-4">
                        A köszönetnyilvánításhoz be kell jelentkezned!
                      </p>
                    )}

                    {isLoggedIn && !userHasValidName && (
                      <p className="text-center bg-destructive/20 text-destructive-foreground p-3 rounded-md mb-4 text-sm">
                        A köszönetnyilvánításhoz be kell állítanod egy nevet a profilodban.
                        <Button asChild variant="link" className="font-bold underline text-blue-400 hover:text-blue-300 p-1 h-auto">
                          <Link to="/settings">
                            Kattints ide a beállításhoz!
                          </Link>
                        </Button>
                      </p>
                    )}

                    <Button
                      onClick={handleThankYou}
                      disabled={hasThanked || !isLoggedIn || !userHasValidName || thanksLoading}
                      className="w-full bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground"
                    >
                      <HeartHandshake size={16} className="mr-2" />
                      {hasThanked ? 'Már megköszönted!' : thanksLoading ? 'Betöltés...' : 'Köszönöm a fordítást!'}
                    </Button>

                    <div className="border-t border-border pt-6">
                      <h3 className="text-lg font-semibold mb-3 text-foreground">Akik már megköszönték:</h3>
                      {thankYous.length === 0 ? (
                        <p className="text-muted-foreground text-center">Még senki sem köszönte meg. Légy te az első!</p>
                      ) : (
                        <TooltipProvider>
                          <div className="flex flex-wrap gap-3 justify-center">
                            {thankYous.map((ty) => (
                              <div key={ty.id}>
                                {ty.user !== 'Névtelen' && (
                                  <div className="flex flex-col items-center text-center text-sm text-muted-foreground">
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Avatar className="w-12 h-12 mb-1 cursor-help">
                                          {ty.avatar_url ? (
                                            <AvatarImage src={ty.avatar_url} alt={ty.user} className="object-cover" crossOrigin="anonymous" decoding="async" />
                                          ) : (
                                            <AvatarFallback className="bg-primary text-primary-foreground text-lg">{ty.user.charAt(0)}</AvatarFallback>
                                          )}
                                        </Avatar>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>{ty.user}</p>
                                      </TooltipContent>
                                    </Tooltip>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <span
                                          className={`font-semibold text-xs cursor-help ${ty.admin
                                            ? 'text-red-500'
                                            : ty.translater
                                              ? 'text-green-500'
                                              : 'text-foreground'
                                            }`}
                                        >
                                          {truncateName(ty.user, 8)}
                                        </span>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>{ty.user}</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </TooltipProvider>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="lg:col-span-1 flex flex-col gap-6">
                {download.description && (
                  <Card className="bg-secondary text-secondary-foreground border-border shadow-md">
                    <CardHeader>
                      <CardTitle className="text-xl font-bold text-primary">Leírás</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p
                        className="text-sm text-muted-foreground whitespace-pre-line [&_a]:text-blue-600 [&_a]:underline"
                        dangerouslySetInnerHTML={{
                          __html: download.description.replace(/\n/g, '<br />'),
                        }}
                      />
                    </CardContent>
                  </Card>
                )}
                {creators.length > 0 && (
                  <div className="space-y-6">
                    <h2 className="text-2xl font-bold text-foreground">A fordítás {creators.length === 1 ? 'készítője' : 'készítői'}</h2>
                    {creators.map((creator) => (
                      <Card key={creator.id} className="bg-secondary text-secondary-foreground border-border shadow-md">
                        <CardHeader>
                          <a href={`/translator/${creator.slug}`}>
                            <CardTitle className="text-xl font-bold text-primary">{creator.name}</CardTitle>
                          </a>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="flex items-center gap-3">
                            <a href={`/translator/${creator.slug}`}>
                              <Avatar className="w-16 h-16 border-2 border-primary">
                                <AvatarImage src={creator.avatar} alt={creator.name} className="object-cover" crossOrigin="anonymous" decoding="async" />
                                <AvatarFallback className="bg-primary text-primary-foreground text-xl">{creator.name.charAt(0)}</AvatarFallback>
                              </Avatar>
                            </a>
                            <div>
                              <a href={`/translator/${creator.slug}`}>
                                <h3 className="text-lg font-semibold text-foreground">{creator.name}</h3>
                              </a>
                              <div
                                className="text-sm text-foreground prose dark:prose-invert max-w-none"
                                dangerouslySetInnerHTML={{ __html: creator.description }}
                              />
                            </div>
                          </div>

                          {creator.contactLinks?.length > 0 && (
                            <>
                              <h4 className="text-base font-semibold text-foreground mt-4">{creator.contactLinks?.length === 1 ? 'Elérhetősége' : 'Elérhetőségei'}</h4>
                              <div className="flex flex-wrap gap-2">
                                {creator.contactLinks.map((link: ContactLink, index: number) => (
                                  <Tooltip key={index}>
                                    <TooltipTrigger asChild>
                                      <Button variant="outline" size="icon" className="bg-muted text-white border-border h-9 w-9" asChild>
                                        <a href={link.type.toLowerCase() === 'email' ? `mailto:${link.value}` : link.value} target="_blank" rel="noopener noreferrer">
                                          {getContactLinkIcon(link.type)}
                                        </a>
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>{link.type}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                ))}
                              </div>
                            </>
                          )}

                          {creator.supportLinks?.length > 0 && (
                            <>
                              <h4 className="text-base font-semibold text-foreground mt-4">Támogatás</h4>
                              <div className="flex flex-wrap gap-2 items-center">
                                {creator.supportLinks.map((link: SupportLink, index: number) => (
                                  <Tooltip key={index}>
                                    <TooltipTrigger asChild>
                                      <Button
                                        key={index}
                                        variant="outline"
                                        size="icon"
                                        className="bg-muted text-white border-border h-9 w-9"
                                        asChild
                                      >
                                        <a
                                          href={link.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                        >
                                          {link.iconbootstrap ? (
                                            <span dangerouslySetInnerHTML={{ __html: link.iconbootstrap }} />
                                          ) : (
                                            getSupportLinkIcon(link.type)
                                          )}
                                        </a>
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>{getSupportLinkDisplayName(link)}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                ))}
                              </div>
                            </>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </main>
        <Footer />
        <ImageGallery
          images={exampleImages}
          isOpen={isGalleryOpen} // A modal nyitottságát ez az állapot vezérli
          onOpenChange={setIsGalleryOpen}
          startIndex={selectedImageIndex}
        />
      </div>
    </TooltipProvider>
  );
};

export default DownloadShow;